export const MIN_VALID_RECORDED_AT_MS = Date.parse(
  "2000-01-01T00:00:00.000Z",
);
export const MAX_RECORDED_AT_FUTURE_DRIFT_MS = 24 * 60 * 60 * 1000;

export const isPlausibleRecordedAtMs = (
  timestampMs: number,
  nowMs: number = Date.now(),
): boolean =>
  Number.isFinite(timestampMs) &&
  timestampMs >= MIN_VALID_RECORDED_AT_MS &&
  timestampMs <= nowMs + MAX_RECORDED_AT_FUTURE_DRIFT_MS;

export const coercePlausibleRecordedAtMs = (
  timestampMs: number | null | undefined,
  nowMs: number = Date.now(),
): number | null => {
  if (timestampMs === null || timestampMs === undefined) {
    return null;
  }

  return isPlausibleRecordedAtMs(timestampMs, nowMs) ? timestampMs : null;
};

/**
 * @name getVideoDuration
 *
 * @description Extracts the duration of a video file
 *
 * @param {File} file - The video file to analyze
 * @returns {Promise<number>} The duration of the video in seconds
 */
export const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve(Math.floor(video.duration));
      video.remove();
    };
    video.onerror = function () {
      reject(new Error("Error loading video metadata"));
    };
    video.src = URL.createObjectURL(file);
  });
};

/**
 * @name getFileCreationTime
 *
 * @description Attempts to read the creation (recorded) time from file metadata.
 * For MP4/MOV files this parses the 'mvhd' box inside the 'moov' atom to get the
 * creation_time. Scans both the beginning and end of the file to handle both
 * faststart (moov at start) and non-faststart (moov at end) MP4s. If parsing
 * fails or the format is unsupported, falls back to `file.lastModified`.
 *
 * @param {File} file - The file to inspect
 * @returns {Promise<number | null>} Unix timestamp in milliseconds or null if unknown
 */
export const getFileCreationTime = async (file: File): Promise<number | null> => {
  const nowMs = Date.now();
  const secondsBetween1904and1970 = 2082844800;

  // Scan a DataView buffer for a 'moov' atom and extract 'mvhd' creation_time.
  // Uses a linear scan so it works even when the buffer doesn't start at a box boundary
  // (e.g. when reading a tail chunk of a non-faststart file).
  const parseMoovCreationTime = (dv: DataView): number | null => {
    const bufferLen = dv.byteLength;
    const readString = (off: number, len: number) => {
      let s = "";
      for (let i = 0; i < len; i++)
        s += String.fromCharCode(dv.getUint8(off + i));
      return s;
    };

    // Scan byte-by-byte for the "moov" four-character code
    for (let i = 4; i + 8 <= bufferLen; i++) {
      if (readString(i, 4) !== "moov") continue;

      // Box starts 4 bytes before the type field
      const boxStart = i - 4;
      let size = dv.getUint32(boxStart);
      let headerLen = 8;

      if (size === 1) {
        // 64-bit large size
        if (boxStart + 16 > bufferLen) continue;
        const hi = dv.getUint32(boxStart + 8);
        const lo = dv.getUint32(boxStart + 12);
        size = hi * 4294967296 + lo;
        headerLen = 16;
      } else if (size === 0) {
        size = bufferLen - boxStart;
      }

      // Search within 'moov' for 'mvhd', clamping to the buffer boundary
      let moovOffset = boxStart + headerLen;
      const moovEnd = Math.min(boxStart + size, bufferLen);

      while (moovOffset + 8 <= moovEnd) {
        const childSize = dv.getUint32(moovOffset);
        const childType = readString(moovOffset + 4, 4);

        if (childType === "mvhd") {
          const version = dv.getUint8(moovOffset + 8);
          if (version === 0 && moovOffset + 16 <= bufferLen) {
            const creationSeconds = dv.getUint32(moovOffset + 12);
            if (creationSeconds !== 0) {
              const unixSec = creationSeconds - secondsBetween1904and1970;
              const ts = coercePlausibleRecordedAtMs(unixSec * 1000, nowMs);
              if (ts !== null) return ts;
            }
          } else if (version === 1 && moovOffset + 20 <= bufferLen) {
            const hi = dv.getUint32(moovOffset + 12);
            const lo = dv.getUint32(moovOffset + 16);
            const creationSeconds = hi * 4294967296 + lo;
            if (creationSeconds !== 0) {
              const unixSec = creationSeconds - secondsBetween1904and1970;
              const ts = coercePlausibleRecordedAtMs(unixSec * 1000, nowMs);
              if (ts !== null) return ts;
            }
          }
          break; // Only one mvhd per moov
        }

        // Guard against malformed boxes
        if (!childSize || childSize < 8) break;
        moovOffset += childSize;
      }
    }

    return null;
  };

  try {
    const chunkSize = 200 * 1024; // 200KB

    // Try the beginning of the file (faststart / streaming-optimized MP4s)
    const headDv = new DataView(await file.slice(0, chunkSize).arrayBuffer());
    const headResult = parseMoovCreationTime(headDv);
    if (headResult !== null) return headResult;

    // Try the end of the file (non-faststart MP4s where moov is written last)
    if (file.size > chunkSize) {
      const tailDv = new DataView(
        await file.slice(file.size - chunkSize).arrayBuffer(),
      );
      const tailResult = parseMoovCreationTime(tailDv);
      if (tailResult !== null) return tailResult;
    }
  } catch (e) {
    // Non-fatal: fall through to fallback
  }

  // Fallback to lastModified if available; null when that too is implausible
  // (per the contract above — callers omit recordedAt rather than stamping
  // upload time as a fake recording time).
  return coercePlausibleRecordedAtMs(file.lastModified, nowMs);
};
